// H-16, H-17, H-18, H-21, H-22: one candidate, as the owner reviews them.
//
// What the owner needs to make a decision and the evidence behind it, one tab
// each: the level on each capability with Elliott's Assessment Analysis and CV
// Analysis (H-17), the team's notes, the transcript, the application and
// the status timeline.
//
// Three things on this screen exist because of the law rather than the product:
//
//   EXPORT carries the EXPLANATION (L-7), its own sheet in the workbook, which is
//   how the customer answers a candidate who asks how AI was used on them, for a
//   human review, or to contest the decision. It stays here after the round
//   closes, until retention ends.
//
//   The INTEGRITY SIGNALS are labelled context, not evidence (L-6), wherever
//   they appear, and each carries a tooltip saying people do these things for
//   many reasons.
//
//   DELETE ENTIRELY (H-22) needs the word typed out, because it erases the
//   person's account, their CV and their transcripts and cannot be undone. It
//   is also how an erasure request is met.

import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { hiringApi, hiringMessage, type HiringRoundView } from '../../../../api/hiring';
import { useHiringCandidate, useHiringOrg, useRefreshHiring } from '../../../../queries/useHiring';
import {
  ActionButton,
  BODY,
  BRANDY,
  DANGER,
  Field,
  FONT,
  INPUT_SX,
  LABEL,
  LINE,
  Note,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  WELL
} from '../capability-setup/designSystem';
import { TabButton } from '../Program/components/program tabs/InsightsPage';
import {
  Detail,
  GRID_LINE,
  IntegrityCaption,
  LevelPill,
  Panel,
  RoleplayContext,
  StatusText,
  when
} from './hiringParts';
import { exportCandidateWorkbook } from './hiringExport';
import CandidateNotes from './CandidateNotes';
import TranscriptPanel from './TranscriptPanel';
import CapabilityMarkers from './CapabilityMarkers';
import { LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

type Tab = 'capabilities' | 'notes' | 'transcript' | 'details' | 'timeline';
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'capabilities', label: 'Capabilities' },
  { key: 'notes', label: 'Notes' },
  { key: 'transcript', label: 'Transcript' },
  { key: 'details', label: 'Details' },
  { key: 'timeline', label: 'Timeline' }
];
const DIVIDE = { width: '1px', height: 16, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' };
const LINE_DIVIDE = { width: '1px', height: 14, flexShrink: 0, bgcolor: '#3A3A3A' };

/** Saves a response as a file without leaving the page. */
const saveBlob = (content: Blob, name: string) => {
  const url = window.URL.createObjectURL(content);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const CandidateAnalysisModal: React.FC<{
  candidateId: string | null;
  round: HiringRoundView;
  onClose: () => void;
}> = ({ candidateId, round, onClose }) => {
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const query = useHiringCandidate(candidateId || undefined);
  const candidate = query.data;
  const [tab, setTab] = useState<Tab>('capabilities');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const deleteConfirmed = confirmDelete.trim().toLowerCase() === 'delete';

  // Every candidate opens on their capabilities: that is what the owner came to read.
  useEffect(() => {
    if (candidateId) setTab('capabilities');
  }, [candidateId]);

  useEffect(() => {
    setWorkEmail(candidate?.workEmail || '');
    setConfirmDelete('');
    setError(null);
  }, [candidate?.id, candidate?.workEmail]);

  const run = async (label: string, work: () => Promise<unknown>, after?: () => void) => {
    setBusy(label);
    setError(null);
    try {
      await work();
      refresh({ roundId: round.id, candidateId: candidateId || undefined });
      after?.();
    } catch (err) {
      setError(hiringMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const downloadCv = () =>
    run('cv', async () => {
      const response = await axios.get(hiringApi.cvUrl(candidate!.id, org), { responseType: 'blob' });
      saveBlob(response.data as Blob, candidate!.cvFileName || 'cv');
    });

  const exportCandidate = () =>
    run('export', async () => {
      const [transcript, explanation] = await Promise.all([
        hiringApi.transcript(candidate!.id, org),
        hiringApi.explanation(candidate!.id, org)
      ]);
      exportCandidateWorkbook(candidate!, round, transcript, explanation);
    });

  return (
    <Dialog
      open={Boolean(candidateId)}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: SURFACE,
          border: `1px solid ${LINE}`,
          borderRadius: '8px',
          [MOBILE_SX]: { height: '100%', m: 0, maxHeight: '100%', borderRadius: 0 }
        }
      }}
    >
      {query.isLoading || !candidate ? (
        <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
          <Bone width="40%" height={24} />
          <Bone height={120} />
          <Bone height={120} />
        </Box>
      ) : (
        <>
          <Box sx={{ flexShrink: 0, p: '20px 24px 14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <Box
                sx={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
              >
                <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
                  {candidate.name}
                </Typography>
                <Box sx={LINE_DIVIDE} />
                <StatusText status={candidate.status} />
                <Box sx={LINE_DIVIDE} />
                <Typography sx={{ ...BODY, fontSize: 12.5, overflowWrap: 'anywhere' }}>{candidate.email}</Typography>
              </Box>
              <ActionButton disabled={busy === 'export'} onClick={exportCandidate}>
                {busy === 'export' ? 'Exporting...' : 'Export'}
              </ActionButton>
              <ActionButton onClick={onClose}>Close</ActionButton>
            </Box>
            <Box
              role="tablist"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                p: '10px 14px',
                bgcolor: WELL,
                border: `1px solid ${LINE}`,
                borderRadius: '8px',
                overflowX: 'auto'
              }}
            >
              {TABS.map((item, i) => (
                <React.Fragment key={item.key}>
                  {i > 0 && <Box sx={DIVIDE} />}
                  <TabButton label={item.label} size={14} active={tab === item.key} onClick={() => setTab(item.key)} />
                </React.Fragment>
              ))}
            </Box>
            {error && (
              <Typography sx={{ ...BODY, color: DANGER }} role="alert">
                {error}
              </Typography>
            )}
          </Box>

          <Box role="tabpanel" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '0 24px 24px' }}>
            {tab === 'capabilities' &&
              (candidate.capabilities.length === 0 ? (
                <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>Nothing assessed yet.</Typography>
              ) : (
                // H-17: the levels, with the evidence behind each one.
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* L-6: wherever an integrity signal shows, so does the caption. */}
                  <IntegrityCaption />
                  {candidate.capabilities.map((capability) => (
                    <Box
                      key={capability.capabilityId}
                      sx={{
                        p: '14px 16px',
                        borderRadius: '4px',
                        bgcolor: WELL,
                        border: `1px solid ${LINE}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        // The sections under the title, a grey line between each, as in the round's table.
                        '& > * + * + *': { borderTop: GRID_LINE, pt: '12px' }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          pb: '10px',
                          borderBottom: '1px solid rgba(247, 148, 32, 0.45)'
                        }}
                      >
                        <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BRANDY, flex: 1 }}>
                          {capability.capabilityName}
                        </Typography>
                        <LevelPill level={capability.level} label={capability.label} />
                      </Box>
                      {/* The rebuild: a dropped, provisional or overridden level says so in words. */}
                      {capability.label && capability.level && capability.label !== LEVEL_NAME[capability.level] && (
                        <Typography data-testid={`level-label-${capability.capabilityId}`} sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>
                          {capability.label}
                        </Typography>
                      )}
                      {capability.attemptsUsed > 0 && (
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              flexWrap: 'wrap',
                              mb: '6px'
                            }}
                          >
                            <Typography sx={{ ...LABEL, color: TEXT, flex: 1, minWidth: 'fit-content' }}>
                              Roleplay Analysis
                            </Typography>
                            <RoleplayContext capability={capability} attempts={round.attempts} />
                          </Box>
                          {capability.analysis && (
                            <Typography sx={{ ...BODY, color: TEXT_2 }}>{capability.analysis}</Typography>
                          )}
                        </Box>
                      )}
                      {capability.cvAnalysis && (
                        <Box>
                          <Typography sx={{ ...LABEL, color: TEXT, mb: '6px' }}>CV Analysis</Typography>
                          <Typography sx={{ ...BODY, color: TEXT_2 }}>{capability.cvAnalysis}</Typography>
                        </Box>
                      )}
                      {capability.roleplayLevel && (
                        <CapabilityMarkers
                          capability={capability}
                          round={round}
                          candidateId={candidate.id}
                          org={org}
                          readOnly={candidate.status === 'hired'}
                          onChanged={() => refresh({ roundId: round.id, candidateId: candidate.id })}
                        />
                      )}
                      {!capability.roleplayLevel && capability.quiz.passed !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Typography sx={{ ...LABEL, color: TEXT }}>Quiz Question</Typography>
                          {capability.quiz.passed ? (
                            <CheckBoxIcon aria-label="Correct" sx={{ fontSize: 18, color: '#22C55E' }} />
                          ) : (
                            <CloseIcon aria-label="Incorrect" sx={{ fontSize: 18, color: DANGER }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}

            {tab === 'notes' && <CandidateNotes candidate={candidate} roundId={round.id} />}

            {tab === 'transcript' && <TranscriptPanel candidateId={candidate.id} />}

            {tab === 'details' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Panel>
                  <Typography sx={{ ...LABEL, color: TEXT_2 }}>Application</Typography>
                  <Detail label="Country of residence">
                    {candidate.residence
                      ? [candidate.residence.country, candidate.residence.state].filter(Boolean).join('-')
                      : 'Not given yet'}
                  </Detail>
                  {Object.entries(candidate.answers).map(([key, value]) => {
                    const field = round.fields.find((f) => f.key === key);
                    return (
                      <Detail key={key} label={field?.label || key}>
                        {value || '-'}
                      </Detail>
                    );
                  })}
                  {candidate.linkedinUrl && <Detail label="LinkedIn">{candidate.linkedinUrl}</Detail>}
                  {round.fields.some((f) => f.key === 'cv' && f.required) && (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {candidate.hasCv ? (
                        <ActionButton disabled={busy === 'cv'} onClick={downloadCv}>
                          {busy === 'cv' ? 'Fetching...' : `Open CV (${candidate.cvFileName || 'file'})`}
                        </ActionButton>
                      ) : (
                        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>No CV uploaded.</Typography>
                      )}
                    </Box>
                  )}
                </Panel>

                {candidate.status === 'hired' && (
                  <Panel>
                    <Typography sx={{ ...LABEL, color: TEXT_2 }}>Company email</Typography>
                    <Typography sx={{ ...BODY, color: TEXT }}>
                      {candidate.workEmail}
                      {candidate.workEmailVerified ? ' · verified' : ' · not verified yet'}
                    </Typography>
                    {!candidate.workEmailVerified && (
                      <>
                        <Note>
                          Single sign-on starts working once they verify this address. Until then you can correct a typo
                          here, which resends the verification.
                        </Note>
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Field label="Correct it">
                              <Box
                                component="input"
                                type="email"
                                value={workEmail}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWorkEmail(e.target.value)}
                                sx={INPUT_SX}
                              />
                            </Field>
                          </Box>
                          <ActionButton
                            disabled={busy === 'workEmail' || workEmail.trim() === candidate.workEmail}
                            onClick={() =>
                              run('workEmail', () => hiringApi.correctWorkEmail(candidate.id, workEmail.trim(), org))
                            }
                          >
                            {busy === 'workEmail' ? 'Saving...' : 'Save and Resend'}
                          </ActionButton>
                        </Box>
                      </>
                    )}
                  </Panel>
                )}

                {/* H-22: permanent, and the way an erasure request is met. */}
                <Panel>
                  <Typography sx={{ ...LABEL, color: TEXT_2 }}>Delete this candidate</Typography>
                  <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3 }}>
                    Deleting here erases everything, including their account, their CV, their attempts and their
                    transcripts, and cannot be undone. You can use this if a candidate requests you to delete their
                    data.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box
                      component="input"
                      value={confirmDelete}
                      placeholder="Type Delete"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmDelete(e.target.value)}
                      sx={{ ...INPUT_SX, width: 160, borderColor: deleteConfirmed ? DANGER : undefined }}
                    />
                    <ActionButton
                      disabled={!deleteConfirmed || busy === 'delete'}
                      reason={!deleteConfirmed ? 'Type Delete to confirm' : undefined}
                      onClick={() => run('delete', () => hiringApi.deleteCandidate(candidate.id, org), onClose)}
                    >
                      {busy === 'delete' ? 'Deleting...' : 'Delete'}
                    </ActionButton>
                  </Box>
                </Panel>
              </Box>
            )}

            {tab === 'timeline' && (
              <Panel>
                <Detail label="Invited">{when(candidate.invitedAt)}</Detail>
                <Detail label="Notice acknowledged">
                  {candidate.noticeAt ? `${when(candidate.noticeAt)} (version ${candidate.noticeVersion})` : 'Not yet'}
                </Detail>
                <Detail label="Started">{when(candidate.startedAt)}</Detail>
                <Detail label="Completed">{when(candidate.completedAt)}</Detail>
                {candidate.removedAt && <Detail label="Removed">{when(candidate.removedAt)}</Detail>}
                {candidate.hiredAt && <Detail label="Hired">{when(candidate.hiredAt)}</Detail>}
                <Detail label="Data Retention">{candidate.retention} after the round closes</Detail>
              </Panel>
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default CandidateAnalysisModal;
